import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, FileText, History, Activity, AlertTriangle } from 'lucide-react';
import api from '../../../utils/api';
import MainLayout from '../../../components/layout/MainLayout';
import { useAuth } from '../../../context/AuthContext';

const WWebBulkHome = () => {
  const navigate = useNavigate();
  const [sessionStatus, setSessionStatus] = useState('not_initialized');

  useEffect(() => {
    fetchSessionStatus();
  }, []);

  const fetchSessionStatus = async () => {
    try {
      const res = await api('/whatsapp-web/session/status');
      if (res.ok) {
        const data = await res.json();
        setSessionStatus(data.status);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const isConnected = sessionStatus === 'connected';

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>WhatsApp Web Bulk Messaging</h1>
            <p style={{ color: 'var(--color-text-secondary)' }}>Send automated, human-like bulk messages through your connected WhatsApp Web session.</p>
          </div>
          
          {!isConnected && (
             <div style={{ background: 'var(--color-background-warning)', color: '#854d0e', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500' }}>
               <AlertTriangle size={18} />
               WhatsApp Web is not connected. Connect it first in the settings.
             </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '40px' }}>
          
          {/* New Campaign Card */}
          <div 
            onClick={() => isConnected ? navigate('/automation/whatsapp-web/bulk/new') : null}
            style={{ 
              background: 'white', 
              padding: '24px', 
              borderRadius: '12px', 
              border: `2px solid ${isConnected ? 'var(--color-brand-primary)' : 'var(--color-border-primary)'}`,
              cursor: isConnected ? 'pointer' : 'not-allowed',
              opacity: isConnected ? 1 : 0.6,
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <div style={{ background: 'var(--color-background-primary)', padding: '12px', borderRadius: '12px', marginBottom: '16px' }}>
              <Play size={24} color="var(--color-brand-primary)" fill="var(--color-brand-primary)" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Launch New Campaign</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>Select leads, pick a template, and start a new bulk sending job with anti-ban protection.</p>
          </div>

          {/* Templates Card */}
          <div 
            onClick={() => navigate('/automation/whatsapp-web/templates')}
            style={{ 
              background: 'white', 
              padding: '24px', 
              borderRadius: '12px', 
              border: '1px solid var(--color-border-primary)',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <div style={{ background: 'var(--color-background-secondary)', padding: '12px', borderRadius: '12px', marginBottom: '16px' }}>
              <FileText size={24} color="var(--color-text-primary)" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Manage Templates</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>Create and edit message templates with dynamic variables for personalization.</p>
          </div>

          {/* Job History Card */}
          <div 
            onClick={() => navigate('/automation/whatsapp-web/bulk/jobs')}
            style={{ 
              background: 'white', 
              padding: '24px', 
              borderRadius: '12px', 
              border: '1px solid var(--color-border-primary)',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <div style={{ background: 'var(--color-background-secondary)', padding: '12px', borderRadius: '12px', marginBottom: '16px' }}>
              <History size={24} color="var(--color-text-primary)" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Campaign History</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>View past and currently running bulk jobs, check progress, and see delivery reports.</p>
          </div>

        </div>

        {/* Anti-Ban Info Section */}
        <div style={{ background: 'var(--color-background-info)', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-info)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', color: 'var(--color-brand-primary)' }}>
            <Activity size={24} />
            <h3 style={{ fontSize: '18px', fontWeight: '600' }}>Anti-Ban Protection Active</h3>
          </div>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '15px', lineHeight: '1.5', marginBottom: '16px' }}>
            To protect your connected WhatsApp number from being flagged or banned, all campaigns sent through this module automatically use our intelligent anti-ban engine:
          </p>
          <ul style={{ color: 'var(--color-text-secondary)', fontSize: '14px', lineHeight: '1.6', paddingLeft: '24px' }}>
            <li><strong>Randomized Delays:</strong> Every message is delayed by a random amount of time (e.g. 8-20 seconds) to avoid robotic patterns.</li>
            <li><strong>Batch Cooling:</strong> Messages are sent in small batches (e.g. 10 at a time), followed by a cool-down period.</li>
            <li><strong>Typing Simulation:</strong> The system fakes "typing..." presence before sending the message.</li>
            <li><strong>Invalid Number Filter:</strong> Checks if the number exists on WhatsApp before attempting to send.</li>
          </ul>
        </div>
      </div>
    </MainLayout>
  );
};

export default WWebBulkHome;
